import { computed, defineComponent, ref, watch } from 'vue';
import SAvatarStemdoer from '@/components/stemdo-components/s-avatar-stemdoer/SAvatarStemdoer.vue';
import { useI18N } from '@/plugins/i18n';
import SPagination from '@/components/stemdo-components/s-pagination/SPagination.vue';
import SSkeletonTable from '@/components/stemdo-components/s-skeleton-table/SSkeletonTable.vue';
import { LabelStyles } from '@/components/stemdo-components/s-label/sLabel';
import SLabel from '@/components/stemdo-components/s-label/SLabel.vue';
import { Stemdoer } from '@/shared/model/stemdoer.model';
import { useRouter } from '@/plugins/router';
import { StemdoerStatus } from '@/shared/model/enumerations/stemdoer-status.model';

export default defineComponent({
  props: {
    stemdoers: {
      type: Array,
      required: true
    },
    displaySkeleton: {
      type: Boolean,
      default: true
    },
    companyItems: {
      type: Array as () => Array<{ id: string; desc: string }>,
      required: false,
      default: () => []
    },
    projectItems: {
      type: Array as () => Array<{ id: string; desc: string; cif?: string }>,
      required: false,
      default: () => []
    }
  },
  components: {
    SAvatarStemdoer,
    SPagination,
    SSkeletonTable,
    SLabel
  },
  setup(props) {
    const i18n = useI18N();
    const router = useRouter();
    const ITEMS_PER_PAGE = 12;
    const offset = ref(0);
    const stemdoersInPage = computed(() => {
      const start = offset.value * ITEMS_PER_PAGE;
      const end = start + ITEMS_PER_PAGE;
      return props.stemdoers.slice(start, end).map(item => {
        const stemdoer = item as Stemdoer;
        // Cohort end date
        let cohortEndDate = null;
        if (stemdoer.cohorts && stemdoer.cohorts[0] && stemdoer.cohorts[0].endDate) {
          cohortEndDate = stemdoer.cohorts[0].endDate;
        }
        // Company name (obtenido por el cif del project y comparado con companyItems)
        let companyName = '-';
        if (stemdoer.currentProject && stemdoer.currentProject.projectId) {
          companyName = getCompanyNameByProject(stemdoer.currentProject.projectId);
        }
        // Project name
        let projectName = '-';
        if (stemdoer.currentProject && stemdoer.currentProject.projectId) {
          projectName = getProjectName(stemdoer.currentProject.projectId);
        }
        // Rate
        let rate = '-';
        if (stemdoer.currentProject && stemdoer.currentProject.rate) {
          rate = String(stemdoer.currentProject.rate);
        }
        return {
          ...stemdoer,
          fullName: `${stemdoer.name ?? ''} ${stemdoer.surname ?? ''}`.trim(),
          cohortEndDate,
          companyName,
          projectName,
          rate
        };
      });
    });

    type StatusLabel = 'active' | 'inactive' | 'preHirable';

    watch(
      () => props.stemdoers,
      () => {
        offset.value = 0;
      }
    );

    const headers = computed(() => [
      { text: i18n.t('stemdoers-filter-list.table.name'), value: 'fullName', width: '20%' },
      { text: i18n.t('stemdoers-filter-list.table.vertical'), value: 'verticalName', width: '10%' },
      { text: i18n.t('stemdoers-filter-list.table.title'), value: 'position', width: '17%' },
      { text: i18n.t('stemdoers-filter-list.table.cohort-end-date'), value: 'cohortEndDate', width: '10%' },
      { text: i18n.t('stemdoers-filter-list.table.company'), value: 'companyName', width: '16%' },
      { text: i18n.t('stemdoers-filter-list.table.project'), value: 'projectName', width: '16%' },
      { text: i18n.t('stemdoers-filter-list.table.rate'), value: 'rate', width: '1%' },
      { text: i18n.t('stemdoers-filter-list.table.email'), value: 'email', width: '20%' },
      { text: i18n.t('stemdoers-filter-list.table.status'), value: 'hide' }
    ]);

    const formatDate = (date: string) => {
      if (!date) return null;
      return i18n.d(new Date(date));
    };

    const isOnTraining = (status: StemdoerStatus) => {
      return status === StemdoerStatus.ON_TRAINING;
    };

    const getStatusDetails = (status: 'active' | 'inactive' | 'preHirable') => {
      let translation = '';
      let labelStyle = '';
      switch (status) {
        case 'inactive':
          translation = i18n.t('stemdoers-filter-list.status.inactive') as string;
          labelStyle = LabelStyles.Senary;
          break;
        case 'active':
          translation = i18n.t('stemdoers-filter-list.status.active') as string;
          labelStyle = LabelStyles.Tertiary;
          break;
        case 'preHirable':
          translation = i18n.t('stemdoers-filter-list.status.preHirable') as string;
          labelStyle = LabelStyles.Quinary;
          break;
        default:
          break;
      }
      return { translation, labelStyle };
    };

    const getStatusForLabel = (item: any): StatusLabel => {
      if (item.availability?.preHirable === true) {
        return 'preHirable';
      }
      return item.hide === true ? 'inactive' : 'active';
    };

    const onPageChange = (newPage: number) => {
      offset.value = newPage - 1;
    };

    const onRowClick = (stemdoer: Stemdoer) => {
      router.push({ name: 'stemdoersByIdManager', params: { id: stemdoer.id.toString() } });
    };

    const getVerticalTranslation = (item: any) => {
      const key = 'stemdoers-filter-list.vertical.' + item.vertical.name.toLowerCase().replace(/\s+/g, '');
      const translation = i18n.t(key);
      return translation !== key ? translation : '-';
    };

    const getItemFieldById = (items: Array<any> | { value: Array<any> }, id: string, field: string): string => {
      let list: Array<any> = Array.isArray(items) ? items : (items as any).value;
      const found = list?.find(c => c.id === id);
      return found && found[field] ? found[field] : '-';
    };

    const getCompanyName = (cif: string) => {
      const found = props.companyItems.find((c: any) => c.cif === cif || c.id === cif);
      return found ? found.desc : '-';
    };

    const getProjectName = (id: string) => getItemFieldById(props.projectItems, id, 'desc');

    const getProjectCif = (id: string): string => {
      return getItemFieldById(props.projectItems, id, 'cif');
    };

    const getCompanyNameByProject = (projectId: string) => {
      const cif = getProjectCif(projectId);
      const foundCompany = props.companyItems.find((c: any) => c.cif === cif || c.id === cif);
      return foundCompany ? foundCompany.desc : '-';
    };

    return {
      headers,
      stemdoersInPage,
      ITEMS_PER_PAGE,
      formatDate,
      onPageChange,
      getStatusDetails,
      getStatusForLabel,
      onRowClick,
      isOnTraining,
      getVerticalTranslation,
      getCompanyName,
      getCompanyNameByProject,
      getProjectName,
      getProjectCif
    };
  }
});
